<template>
<q-page class="main no-sticky-header">
  <div class="justify-center items-center" :class="{'q-pa-md': !$isDesktop }">
    <q-card class="row">
      <q-stepper
          v-model="step"
          ref="stepper"
          header-nav
          :contracted="!$isDesktop"
          :vertical="!$isMobile"
          class="col-sm-1 col-md-2 col-12"
          color="primary"
          animated
          @transition="moveFocusToCurrentStep"
      >
        <q-step is-update
                :name="STEP_MAP"
                :done="mapComplete"
                header-nav
                title="Geef de locatie aan"
                :caption="$errorMessage('geometry') || 'Teken de locatie van het project in op de kaart'"
                :error="$hasError('geometry')"
                icon="location_on"
                active-icon="location_on"
        />
        <q-step
            :name="STEP_CATEGORIES"
            :header-nav="mapComplete"
            :done="categoriesComplete"
            title="Selecteer de categorie&euml;n"
            :caption="$errorMessage('category_ids') || 'Geef aan welke catgeorie&euml;n er van toepassing zijn op het project'"
            :error="$hasError('category_ids')"
            icon="interests"
            active-icon="interests"
        />
        <q-step
            :name="STEP_PHASES"
            :header-nav="phasesComplete"
            :done="phasesComplete"
            title="Selecteer de fasen"
            :caption="$errorMessage('phase_ids') || 'Geef aan welke fase(n) er van toepassing zijn op het project'"
            :error="$hasError('phase_ids')"
            icon="pending_actions"
            active-icon="pending_actions"
        />
        <q-step
            v-if="results"
            :name="STEP_RESULTS"
            :header-nav="!hasChanged"
            header-nav
            :done="!hasChanged"
            title="Resultaten"
            caption="Bekijk de gevonden richtlijnen"
            icon="playlist_add_check"
            active-icon="playlist_add_check"
        />
      </q-stepper>
      <div class="col-sm-11 col-md-10 col-12">
        <common-form ref="form" id="search-content">
          <input v-if="hash"
                 type="hidden"
                 name="hash"
                 :value="hash" />
          <input v-if="email"
                 type="hidden"
                 name="email"
                 :value="email" />
          <search-map
              :visible="step === STEP_MAP"
              :class="{'visible-search-step': step === STEP_MAP}"
              v-model:geometry="geometry" />
          <item-selection
              v-show="step === STEP_CATEGORIES"
              :class="{'visible-search-step': step === STEP_CATEGORIES}"
              :rows="2"
              v-model="category_ids"
              :items="categories"
              name="category_ids[]" />
          <item-selection
              v-show="step === STEP_PHASES"
              :class="{'visible-search-step': step === STEP_PHASES}"
              v-model="phase_ids"
              :rows="1"
              :items="phases"
              name="phase_ids[]" />
          <search-results
              v-if="results"
              v-show="step === STEP_RESULTS"
              :class="{'visible-search-step': step === STEP_RESULTS}"
              :results="results"
              :queried-phases="phase_ids"
          />
          <q-card-actions
              :class="{'q-card__actions--backable': step !== STEP_MAP, 'q-pa-md': $isMobile, 'q-py-md': !$isMobile}">
            <accent-button
                v-if="showSubmitButton"
                type="submit"
                :disabled="!allStepsComplete"
                @click="$refs.form.submit($route('search.get-results'), '_self')"
                :label="results ? 'Vernieuw resultaten' : 'Zoek de richtlijnen'" />
            <primary-button v-if="showNextButton"
                            @click="$refs.stepper.next()"
                            label="Volgende"
                            :disabled="!stepComplete(step)" />
            <template v-if="showResultButtons">
              <accent-button v-if="creationEnabled && ($can('create-projects') || $guest())"
                             @click="$refs.form.submit($route('search.add-to-user'), '_self')"
                             label="Sla op bij uw account" :dense="$isMobile" />
              <accent-button v-else
                           @click="$refs.form.submit($route('search.print', { type: 'pdf' }),'_blank')"
                           label="Print" />
              <template v-if="!$isMobile">
                <flat-button @click="sendResultsByEmail"
                             label="Mail de resultaten" />
                <flat-button v-if="creationEnabled && ($can('create-projects') || $guest())"
                             @click="$refs.form.submit($route('search.print', { type: 'pdf' }),'_blank')"
                             label="Print" />
                <flat-button @click="$refs.form.submit($route('search.print', { type: 'excel' }),'_blank')"
                             label="Exporteer" />
              </template>
              <template v-else>
                <q-space />
                <icon-button
                    icon="more_vert"
                    text-color="neutral-90"
                    size="sm"
                    aria-label="Toon menu"
                    round
                    outline
                    @click="showMenu = true"
                />
                <q-dialog v-model="showMenu" position="bottom">
                  <q-card>
                    <q-list separator>
                      <nav-list-item
                          label="Mail de resultaten"
                          action
                          @click="sendResultsByEmail"
                      />
                      <nav-list-item
                          label="Print"
                          action
                          @click="$refs.form.submit($route('search.print', { type: 'pdf' }),'_blank')"
                      />
                      <nav-list-item
                          label="Exporteer"
                          action
                          @click="$refs.form.submit($route('search.print', { type: 'excel' }),'_blank')"
                      />
                    </q-list>
                  </q-card>
                </q-dialog>
              </template>
            </template>
            <icon-button
                v-if="showBackButton"
                @click="$refs.stepper.previous()"
                aria-label="Terug"
                round
                icon="arrow_back_ios_new"
                :size="$isMobile ? 'sm' : 'md'"
                flat />
          </q-card-actions>
        </common-form>
      </div>
    </q-card>
  </div>
</q-page>
</template>


<script>
import SearchResults from './SearchResults'
import AccentButton from '~HIOR/components/button/AccentButton'

const STEP_MAP = 1
const STEP_CATEGORIES = 2
const STEP_PHASES = 3
const STEP_RESULTS = 4
export default {
  props: {
    phases: {
      type: Array,
      default: null,
    },
    categories: {
      type: Array,
      default: null,
    },
    results: {
      type: Object,
      default: null,
    },
    oldSearch: {
      type: Object,
      default: null,
    },
    creationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  components: { AccentButton, SearchResults },
  data () {
    let step
    if (this.$hasError('geometry')) step = STEP_MAP
    else if (this.$hasError('category_ids')) step = STEP_CATEGORIES
    else if (this.$hasError('phase_ids')) step = STEP_PHASES
    else if (this.results) step = STEP_RESULTS
    else step = STEP_MAP

    return {
      step,
      showMenu: false,
      hasChanged: false,
      email: this.$old('email'),
      geometry: this.oldSearch?.geometry || this.$old('geometry', ''),
      phase_ids: (this.oldSearch?.phase_ids || this.$old('phase_ids', [])).map(id => parseInt(id)),
      category_ids: (this.oldSearch?.category_ids || this.$old('category_ids', [])).map(id => parseInt(id)),
      hash: this.oldSearch?.hash || this.$old('hash'),
      STEP_MAP,
      STEP_CATEGORIES,
      STEP_PHASES,
      STEP_RESULTS,
    }
  },
  watch: {
    geometry () {
      if (this.results) {
        this.hasChanged = true
      }
    },
    phase_ids () {
      if (this.results) {
        this.hasChanged = true
      }
    },
    category_ids () {
      if (this.results) {
        this.hasChanged = true
      }
    },
  },
  computed: {
    showNextButton () {
      if (this.step === STEP_RESULTS) {
        return false
      }
      if (this.step !== STEP_PHASES) {
        return true
      }
      return !this.hasChanged && this.results
    },
    showSubmitButton () {
      if (this.step === STEP_RESULTS) {
        return false
      }
      if (this.hasChanged && this.phasesComplete) {
        return true
      }
      return this.step === STEP_PHASES && !this.results
    },
    showResultButtons () {
      return this.step === STEP_RESULTS
    },
    showBackButton () {
      return this.step !== STEP_MAP
    },
    mapComplete () {
      return !!this.geometry
    },
    categoriesComplete () {
      return this.category_ids.length > 0
    },
    phasesComplete () {
      return this.phase_ids.length > 0
    },
    allStepsComplete () {
      return this.mapComplete &&
          this.categoriesComplete &&
          this.phasesComplete
    },
  },
  methods: {
    stepComplete (step) {
      switch (step) {
        case STEP_MAP:
          return this.mapComplete
        case STEP_CATEGORIES:
          return this.categoriesComplete
        case STEP_PHASES:
          return this.phasesComplete
      }
    },
    async sendResultsByEmail () {
      if (!this.results) return
      this.email = await this.$prompt({
        title: 'Deel via e-mail',
        message: 'Vul een of meerdere e-mailadressen in afgescheiden door komma\'s',
        label: 'E-mailadres(sen)',
      })
      if (!this.email) return
      this.$refs.form.submit(this.$route('search.mail'))
    },
    async moveFocusToCurrentStep () {
      await this.$nextTick()
      const firstFocussable = this.$el.querySelector('.visible-search-step button:not(disabled), .visible-search-step [role="checkbox"]')
      if (firstFocussable) {
        firstFocussable.focus()
      }

    },
  },
}
</script>
